import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { PawPrint, Ruler, Utensils, Radio, Settings } from 'lucide-react';
import { useI18n } from '@/lib/i18n';

export const Features = () => {
  const { t } = useI18n();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      ref={ref}
      id="features"
      className="py-24 lg:py-32 bg-surface"
      data-testid="features-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-2xl sm:text-3xl font-black text-primary mb-12 lg:mb-16 tracking-tight"
          data-testid="features-title"
        >
          {t('features.title')}
        </motion.h2>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-5 lg:gap-6">
          {/* Large Featured - Animal Tracking */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="md:col-span-4 lg:col-span-4 bg-white p-8 rounded-2xl border border-primary/5 shadow-soft group overflow-hidden"
            data-testid="feature-animal-tracking"
          >
            <div className="relative z-10">
              <div className="w-12 h-12 bg-forest-100 rounded-xl flex items-center justify-center mb-4">
                <PawPrint className="w-6 h-6 text-forest-700" />
              </div>
              <h3 className="text-2xl font-bold text-primary mb-2">
                {t('features.animalTracking')}
              </h3>
              <p className="text-muted-foreground max-w-md">
                {t('features.animalTrackingDesc')}
              </p>
            </div>
            <div className="mt-10 -mb-8 -mr-8">
              <img
                src="https://images.pexels.com/photos/2887110/pexels-photo-2887110.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600"
                alt="Modern milking parlor"
                className="rounded-tl-2xl object-cover h-48 w-full transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          </motion.div>

          {/* Weight & Growth */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="md:col-span-2 lg:col-span-2 bg-forest-100 p-8 rounded-2xl"
            data-testid="feature-weight-growth"
          >
            <div className="w-10 h-10 bg-forest-200 rounded-lg flex items-center justify-center mb-4">
              <Ruler className="w-5 h-5 text-forest-800" />
            </div>
            <h3 className="text-xl font-bold text-primary mb-2">
              {t('features.weightGrowth')}
            </h3>
            <p className="text-forest-700/80 text-sm">
              {t('features.weightGrowthDesc')}
            </p>
            <div className="mt-8 pt-6 border-t border-forest-200">
              <div className="h-2 w-full bg-forest-200 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={isInView ? { width: '75%' } : {}}
                  transition={{ delay: 0.5, duration: 1, ease: 'easeOut' }}
                  className="h-2 bg-primary rounded-full"
                />
              </div>
              <p className="text-[10px] uppercase font-mono font-bold tracking-[0.2em] mt-2 text-primary">
                {t('features.targetEfficiency')}: 75%
              </p>
            </div>
          </motion.div>

          {/* Feed & Cost Control */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="md:col-span-2 lg:col-span-2 bg-gray-100 p-8 rounded-2xl"
            data-testid="feature-feed-cost"
          >
            <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center mb-4">
              <Utensils className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-primary mb-2">
              {t('features.feedCost')}
            </h3>
            <p className="text-muted-foreground text-sm">
              {t('features.feedCostDesc')}
            </p>
          </motion.div>

          {/* IoT Integration */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="md:col-span-4 lg:col-span-4 bg-white p-8 rounded-2xl border border-primary/5 shadow-soft flex flex-col md:flex-row gap-8 items-center"
            data-testid="feature-iot"
          >
            <div className="flex-1">
              <div className="w-10 h-10 bg-forest-100 rounded-lg flex items-center justify-center mb-4">
                <Radio className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">
                {t('features.iotIntegration')}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {t('features.iotIntegrationDesc')}
              </p>
            </div>
            <div className="w-full md:w-40 aspect-video bg-surface rounded-xl flex items-center justify-center border border-primary/5">
              <Settings className="w-10 h-10 text-primary/20" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
